import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Box, Typography, InputAdornment, Tooltip, IconButton } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { createWasteEntry } from '@state/reducers/wasteEntryReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { MemberDto, MemberService } from '@services/api'
import { ActivityLevel, CreateWasteEntryDto, GeneratorElutionDto, HotLabEquipmentDto, HotLabEquipmentType, IsotopeDto, PreparationLotDto, WasteContainerDto, WasteStatus, WasteType } from '@services/dtos'
import { wasteContainerService } from '@services/wasteContainerService'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import { getWasteContainers, setWasteContainerFilters } from '@state/reducers/wasteContainerReducer'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { FormHeader } from './FormHeader'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import WasteContainerForm from './WasteContainerForm'
import WasteContainerChips from '@components/WasteContainerChips'
import { getNameWithRole } from '@utils/member.utils'
import { wasteEntryFromSourceSchema } from '@utils/schemas'
import InputField from '../inputs/InputField'
import moment from 'moment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import SyncIcon from '@mui/icons-material/Sync'
import HotLabEquipmentForm from './HotLabEquipmentForm'

type Props = {
  elution?: GeneratorElutionDto
  preparation?: PreparationLotDto
  onClose: () => void
}

const WasteEntryFromSourceForm: React.FC<Props> = ({ elution, preparation, onClose }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedContainer, setSelectedContainer] = useState<WasteContainerDto | null>(null)

  const defaultMember = user && 'role' in user ? user as unknown as MemberDto : null
  const containerRef = React.useRef(new Map<string, WasteContainerDto>())

  const initialIsotopes: IsotopeDto[] = elution
    ? [elution.isotope]
    : (preparation?.compound.isotopes ?? [])
  const isotopeId = initialIsotopes[0]?.id
  const containerDefaultValues = {
    type: WasteType.SOLID,
    activityLevel: ActivityLevel.HIGH,
    isotopeIds: initialIsotopes.map((i) => i.id),
  }

  const subTitles = elution
    ? [`${elution.isotope.name} (${elution.isotope.code}) - ${moment(elution.activityRefAt).format('DD/MM/YYYY HH:mm')}`]
    : preparation
      ? [`${preparation.compound.name} - ${moment(preparation.preparedAt).format('DD/MM/YYYY HH:mm')}`]
      : []

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateWasteEntryDto>({
    resolver: yupResolver(wasteEntryFromSourceSchema),
    context: { container: selectedContainer },
    defaultValues: {
      fromElutionId: elution?.id,
      fromPreparationId: preparation?.id,
      activityRefAt: new Date(),
      activityAtRefMbq: preparation
        ? (preparation.activityAtTimeMbq ?? undefined)
        : elution ? (elution.activityAtTimeMbq ?? undefined) : undefined,
    },
  })

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const fetchActivimeters = async (search: string) => {
    const page = await hotLabEquipmentService.getAll({ search, type: HotLabEquipmentType.ACTIVIMETER, limit: 20, page: 0 })
    return page?.datas ?? []
  }

  const fetchOpenContainers = async (search: string) => {
    const page = await wasteContainerService.getAll({
      search,
      status: WasteStatus.OPEN,
      type: WasteType.SOLID,
      isotopeId,
      limit: 20,
      page: 0,
    })
    const list = page?.datas ?? []
    for (const c of list) {
      containerRef.current.set(c.id, c)
    }
    return list
  }

  const onSubmitForm = async (data: CreateWasteEntryDto) => {
    setIsSubmitting(true)
    dispatch(createWasteEntry(data))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'La mise en déchet a été enregistrée avec succès', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() =>
        dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } })),
      )
      .finally(() => setIsSubmitting(false))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={`Mise en déchet ${preparation ? `#${preparation.no}` : elution ? `#${elution.no}` : ''}`}
          subTitles={subTitles}
          submitText="Enregistrer"
        />

        <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
          <Grid item xs={12}>
            <AddWrapperInput
              formMaxWidth="md"
              legend="Conteneur à déchets"
              addTooltipTitle="Ajouter un conteneur"
              dialogContent={(close) => (
                <WasteContainerForm
                  onClose={close}
                  defaultValues={containerDefaultValues}
                  initialIsotopes={initialIsotopes}
                />
              )}
              onAfterClose={() => {
                dispatch(setWasteContainerFilters({ status: WasteStatus.OPEN }))
                dispatch(getWasteContainers())
              }}
              input={
                <Controller
                  name="containerId"
                  control={control}
                  render={({ field: { value: id, onChange } }) => {
                    const selected = id ? (containerRef.current.get(id) ?? null) : null
                    return (
                      <>
                        <ServerAutocomplete<WasteContainerDto>
                          autoFetchOnMount
                          value={selected}
                          fetchService={fetchOpenContainers}
                          onChange={(container) => {
                            onChange(container?.id ?? null)
                            setSelectedContainer(container ?? null)
                          }}
                          getValue={(c) => c?.id ?? null}
                          getOptionLabel={(value) => {
                            const c = value as WasteContainerDto
                            return c ? c.label : ''
                          }}
                          renderOption={(props, c) => (
                            <li {...props} key={c.id}>
                              <Box sx={{ py: 0.25, width: '100%' }}>
                                <Typography variant="body2" fontWeight={400}>{c.label}</Typography>
                                <WasteContainerChips container={c} />
                              </Box>
                            </li>
                          )}
                          placeholder="Rechercher un conteneur ouvert correspondant..."
                          label="Conteneur ouvert"
                          minSearchLength={0}
                          helperText={errors.containerId?.message}
                          error={!!errors.containerId}
                        />
                        {selectedContainer && (
                          <Box className="mt-2">
                            <WasteContainerChips container={selectedContainer} />
                          </Box>
                        )}
                      </>
                    )
                  }}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <InputField
              control={control}
              name="description"
              label="Notes"
              type="text"
              multiline
              rows={1}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              control={control}
              name="activityAtRefMbq"
              label="Activité (MBq)"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label="Heure mesure"
                value={watch('activityRefAt') ? moment(watch('activityRefAt')) : null}
                onChange={(date) => {
                  if (!date || !date.isValid()) {
                    setValue('activityRefAt', undefined)
                    return
                  }
                  setValue('activityRefAt', date.toDate())
                }}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.activityRefAt,
                    helperText: errors.activityRefAt?.message,
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Maintenant" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setValue('activityRefAt', new Date())}
                            >
                              <SyncIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <AddWrapperInput
              legend="Activimètre"
              addTooltipTitle="Ajouter un activimètre"
              dialogContent={(close) => (
                <HotLabEquipmentForm
                  onClose={close}
                  defaultValues={{ type: HotLabEquipmentType.ACTIVIMETER }}
                />
              )}
              input={
                <ServerAutocomplete<HotLabEquipmentDto>
                  autoFetchOnMount
                  fetchService={fetchActivimeters}
                  onChange={(value: HotLabEquipmentDto | null) => setValue('hotLabEquipmentId', value?.id ?? undefined, { shouldValidate: true })}
                  getValue={(value) => value?.id ?? null}
                  getOptionLabel={(value) => (value as HotLabEquipmentDto)?.name ?? ''}
                  debounceMs={300}
                  placeholder="Rechercher un activimètre..."
                  label="Activimètre utilisé pour la mesure"
                  minSearchLength={0}
                  helperText={errors.hotLabEquipmentId?.message}
                  error={!!errors.hotLabEquipmentId}
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              control={control}
              name="massG"
              label="Masse (g)"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ServerAutocomplete<MemberDto>
              defaultValue={defaultMember}
              initialOptions={defaultMember ? [defaultMember] : []}
              value={defaultMember ?? null}
              fetchService={fetchMembers}
              onChange={(value: MemberDto | null) => {
                setValue('collectedById', value?.id)
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => getNameWithRole(value as MemberDto)}
              debounceMs={300}
              placeholder="Rechercher un membre..."
              label="Collecté par"
              helperText={errors.collectedById?.message}
              error={!!errors.collectedById}
              minSearchLength={0}
            />
          </Grid>
        </Grid>
      </form>
    </div>
  )
}

export default WasteEntryFromSourceForm
